import { supabase } from '../lib/supabase';

export const analyticsService = {
  // Get dashboard analytics
  async getDashboardAnalytics(filters = {}) {
    try {
      const { projectId, startDate, endDate, advisorId } = filters;

      let query = supabase?.from('crm_leads')?.select('*');

      if (projectId) {
        query = query?.eq('project_id', projectId);
      }
      if (advisorId) {
        query = query?.eq('advisor_id', advisorId);
      }
      if (startDate) {
        query = query?.gte('created_at', startDate);
      }
      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate metrics
      const totalLeads = data?.length || 0;
      const totalValue = data?.reduce((sum, lead) => sum + (parseFloat(lead?.presupuesto) || 0), 0);
      const conversionRate = totalLeads > 0 ? (data?.filter(l => l?.stage === 'closed_won')?.length / totalLeads) * 100 : 0;

      // Group by stage
      const byStage = data?.reduce((acc, lead) => {
        acc[lead?.stage] = (acc?.[lead?.stage] || 0) + 1;
        return acc;
      }, {});

      // Group by source
      const bySource = data?.reduce((acc, lead) => {
        acc[lead?.fuente] = (acc?.[lead?.fuente] || 0) + 1;
        return acc;
      }, {});

      // Group by interest level
      const byInterest = data?.reduce((acc, lead) => {
        acc[lead?.nivel_interes] = (acc?.[lead?.nivel_interes] || 0) + 1;
        return acc;
      }, {});

      return {
        totalLeads,
        totalValue,
        conversionRate,
        byStage,
        bySource,
        byInterest
      };
    } catch (error) {
      console.error('Error fetching dashboard analytics:', error);
      throw error;
    }
  },

  // Get conversion funnel data
  async getConversionFunnel(projectId) {
    try {
      let query = supabase?.from('crm_leads')?.select('stage, presupuesto');

      if (projectId) {
        query = query?.eq('project_id', projectId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const stages = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'closed_won'];
      const funnelData = stages?.map(stage => {
        const stageLeads = data?.filter(l => l?.stage === stage);
        return {
          stage,
          count: stageLeads?.length,
          value: stageLeads?.reduce((sum, l) => sum + (parseFloat(l?.presupuesto) || 0), 0)
        };
      });

      return funnelData;
    } catch (error) {
      console.error('Error fetching conversion funnel:', error);
      throw error;
    }
  },

  // Get advisor performance
  async getAdvisorPerformance(startDate, endDate) {
    try {
      let query = supabase
        ?.from('crm_leads')
        ?.select(`
          advisor_id,
          stage,
          presupuesto,
          user_profiles!crm_leads_advisor_id_fkey(full_name)
        `);

      if (startDate) {
        query = query?.gte('created_at', startDate);
      }
      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group by advisor
      const advisorStats = {};
      data?.forEach(lead => {
        const advisorId = lead?.advisor_id;
        if (!advisorStats?.[advisorId]) {
          advisorStats[advisorId] = {
            advisorId,
            advisorName: lead?.user_profiles?.full_name || 'Unknown',
            totalLeads: 0,
            closedWon: 0,
            totalValue: 0,
            conversionRate: 0
          };
        }
        advisorStats[advisorId].totalLeads++;
        if (lead?.stage === 'closed_won') {
          advisorStats[advisorId].closedWon++;
          advisorStats[advisorId].totalValue += parseFloat(lead?.presupuesto) || 0;
        }
      });

      // Calculate conversion rates
      Object.values(advisorStats)?.forEach(stats => {
        stats.conversionRate = stats?.totalLeads > 0 ? (stats?.closedWon / stats?.totalLeads) * 100 : 0;
      });

      return Object.values(advisorStats);
    } catch (error) {
      console.error('Error fetching advisor performance:', error);
      throw error;
    }
  },

  // Get campaign ROI
  async getCampaignROI() {
    try {
      const { data, error } = await supabase
        ?.from('marketing_campaigns')
        ?.select(`
          id,
          name,
          platform,
          budget,
          status,
          campaign_analytics(impressions, clicks, conversions, cost_per_lead)
        `);

      if (error) throw error;

      return data?.map(campaign => {
        const analytics = campaign?.campaign_analytics?.[0] || {};
        const totalConversions = analytics?.conversions || 0;
        const totalCost = parseFloat(campaign?.budget) || 0;
        const roi = totalCost > 0 ? ((totalConversions * 1000 - totalCost) / totalCost) * 100 : 0;

        return {
          id: campaign?.id,
          name: campaign?.name,
          platform: campaign?.platform,
          budget: totalCost,
          impressions: analytics?.impressions || 0,
          clicks: analytics?.clicks || 0,
          conversions: totalConversions,
          costPerLead: analytics?.cost_per_lead || 0,
          roi
        };
      });
    } catch (error) {
      console.error('Error fetching campaign ROI:', error);
      throw error;
    }
  },

  // Get time series data
  async getTimeSeriesData(metric, projectId, startDate, endDate) {
    try {
      let query = supabase?.from('crm_leads')?.select('created_at, stage, presupuesto');

      if (projectId) {
        query = query?.eq('project_id', projectId);
      }
      if (startDate) {
        query = query?.gte('created_at', startDate);
      }
      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      const { data, error } = await query?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date
      const timeSeriesData = {};
      data?.forEach(lead => {
        const date = new Date(lead?.created_at)?.toISOString()?.split('T')?.[0];
        if (!timeSeriesData?.[date]) {
          timeSeriesData[date] = { date, count: 0, value: 0, conversions: 0 };
        }
        timeSeriesData[date].count++;
        timeSeriesData[date].value += parseFloat(lead?.presupuesto) || 0;
        if (lead?.stage === 'closed_won') {
          timeSeriesData[date].conversions++;
        }
      });

      return Object.values(timeSeriesData);
    } catch (error) {
      console.error('Error fetching time series data:', error);
      throw error;
    }
  },

  // Export analytics data
  async exportAnalyticsData(filters, format = 'csv') {
    try {
      const analytics = await this.getDashboardAnalytics(filters);
      
      if (format === 'csv') {
        const csvData = this.convertToCSV(analytics);
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL?.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics_${new Date()?.toISOString()}.csv`;
        link?.click();
      }

      return analytics;
    } catch (error) {
      console.error('Error exporting analytics data:', error);
      throw error;
    }
  },

  // Helper: Convert to CSV
  convertToCSV(data) {
    const headers = Object.keys(data);
    const values = Object.values(data)?.map(v => 
      typeof v === 'object' ? JSON.stringify(v) : v
    );
    return `${headers?.join(',')}
${values?.join(',')}`;
  }
};

export default analyticsService;